import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Building2,
  ChevronLeft,
  ChevronRight,
  FileText,
  Filter,
  MapPin,
  Paperclip,
  Search,
  Send,
  Users,
} from 'lucide-react';
import { api } from '@/lib/api';
import { fmtDate, fmtInt, fmtRange } from '@/lib/format';
import { Badge } from '@/components/ui/Badge';
import { DeadlineBadge } from '@/components/DeadlineBadge';
import { EmptyState } from '@/components/EmptyState';
import { Masthead } from '@/components/Masthead';
import { SkeletonLine } from '@/components/Skeleton';
import type { RLPOpportunity } from '@/lib/api-types';

const STATUSES: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'extracted', label: 'Extracted' },
  { value: 'failed', label: 'Failed' },
];

const PAGE_SIZE = 20;

export default function RLPList() {
  const [status, setStatus] = useState<string>('');
  const [q, setQ] = useState<string>('');
  const [sort, setSort] = useState<string>('updated');
  const [page, setPage] = useState<number>(1);
  const [tab, setTab] = useState<'promising' | 'all'>('all');

  // Reset to page 1 when filters/search/sort change so we don't land on an
  // empty page (e.g. filtering from 60 rows to 3 while on page 3).
  useEffect(() => {
    setPage(1);
  }, [status, q, sort, tab]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['rlps', { status, q, sort, page }],
    queryFn: () =>
      api.listRlps({
        status: status || undefined,
        q: q || undefined,
        sort,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const rlps = data?.rlps ?? [];
  const now = Date.now();
  const promising = rlps.filter(
    (r) =>
      ((r.candidate_count ?? 0) > 0 || (r.pending_draft_count ?? 0) > 0) &&
      (r.response_deadline == null || r.response_deadline > now)
  );
  const visible = tab === 'promising' ? promising : rlps;

  const total = data?.total ?? 0;
  const shown = data?.rlps.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstIndex = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastIndex = (page - 1) * PAGE_SIZE + shown;

  return (
    <div className="flex flex-col gap-6">
      <Masthead
        kicker="Intelligence"
        title="Solicitations"
        dateline={
          data
            ? `Showing ${fmtInt(firstIndex)} – ${fmtInt(lastIndex)} of ${fmtInt(total)} results`
            : 'Loading…'
        }
        lede="Every federal lease RLP under observation — deadline-sorted, searchable, and ready for triage. Familiar SAM.gov structure, enriched with extraction status and outreach activity."
      />

      {/* Filter/search bar */}
      <div className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-dim" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, notice ID, agency"
            className="h-10 w-full rounded-lg border border-rule bg-surface pl-9 pr-3 text-sm text-ink placeholder:text-ink-dim focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10"
            aria-label="Search RLPs"
            data-testid="rlp-search"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 rounded-lg border border-rule bg-surface-sunk p-1">
            {STATUSES.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => setStatus(s.value)}
                className={
                  'rounded-md px-3 py-1.5 text-[0.8125rem] font-semibold transition-all ' +
                  (status === s.value
                    ? 'bg-surface text-ink shadow-card'
                    : 'text-ink-muted hover:text-ink')
                }
                data-testid={`status-filter-${s.value || 'all'}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-rule bg-surface px-3 py-1.5 text-[0.8125rem] text-ink-muted">
            <Filter className="h-3.5 w-3.5" />
            <label className="flex items-center gap-1.5">
              <span className="text-ink-dim">Sort by</span>
              <select
                className="cursor-pointer border-0 bg-transparent font-semibold text-ink focus:outline-none"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                aria-label="Sort RLPs"
              >
                <option value="updated">Date Modified/Updated</option>
                <option value="deadline">Offers Due</option>
                <option value="posted">Date Published</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      {isError && (
        <div className="rounded-xl border border-danger-border bg-danger-soft px-5 py-4 text-sm text-danger-bold">
          Failed to load the register: {error instanceof Error ? error.message : 'unknown'}
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card flex flex-col gap-3 p-6">
              <SkeletonLine width="65%" />
              <SkeletonLine width="35%" />
              <SkeletonLine width="80%" />
            </div>
          ))}
        </div>
      ) : rlps.length === 0 ? (
        <EmptyState
          title="No matching records"
          description="Relax a filter or wait — the hunter sweeps SAM.gov every half hour."
        />
      ) : (
        <>
          {/* Promising / All tab switcher */}
          <div
            className="flex gap-2 border-b border-rule"
            role="tablist"
            aria-label="RLP view"
          >
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'all'}
              onClick={() => setTab('all')}
              className={
                tab === 'all'
                  ? 'border-b-2 border-brand-600 px-4 py-2 text-sm font-semibold text-brand-700'
                  : 'border-b-2 border-transparent px-4 py-2 text-sm font-medium text-ink-muted hover:text-ink'
              }
              data-testid="rlp-tab-all"
            >
              All ({fmtInt(total)})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'promising'}
              onClick={() => setTab('promising')}
              className={
                tab === 'promising'
                  ? 'border-b-2 border-brand-600 px-4 py-2 text-sm font-semibold text-brand-700'
                  : 'border-b-2 border-transparent px-4 py-2 text-sm font-medium text-ink-muted hover:text-ink'
              }
              data-testid="rlp-tab-promising"
            >
              Promising ({promising.length})
            </button>
          </div>

          {visible.length === 0 ? (
            <EmptyState
              title="Nothing promising on this page"
              description="No RLPs here have live candidates or pending drafts. Switch to All to see every record."
            />
          ) : (
            <div
              className="flex flex-col gap-4"
              data-testid="rlp-table"
              data-total={total}
              data-shown={shown}
              data-tab={tab}
              data-visible={visible.length}
            >
              {visible.map((r) => (
                <SolicitationCard key={r.id} rlp={r} />
              ))}
            </div>
          )}
          {pageCount > 1 && (
            <Pagination
              page={page}
              pageCount={pageCount}
              onChange={(p) => {
                setPage(p);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

function Pagination({
  page,
  pageCount,
  onChange,
}: {
  page: number;
  pageCount: number;
  onChange: (p: number) => void;
}) {
  const prevDisabled = page <= 1;
  const nextDisabled = page >= pageCount;
  const pages = pageWindow(page, pageCount);
  return (
    <nav
      aria-label="Pagination"
      className="flex items-center justify-between gap-3 border-t border-rule pt-4"
      data-testid="rlp-pagination"
    >
      <button
        type="button"
        onClick={() => !prevDisabled && onChange(page - 1)}
        disabled={prevDisabled}
        className="inline-flex items-center gap-1 rounded-md border border-rule bg-surface px-3 py-1.5 text-[0.8125rem] font-semibold text-ink-muted transition-colors hover:border-brand-300 hover:text-ink disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-rule disabled:hover:text-ink-muted"
      >
        <ChevronLeft className="h-3.5 w-3.5" /> Prev
      </button>
      <ol className="flex items-center gap-1">
        {pages.map((p, i) =>
          p === '…' ? (
            <li key={`gap-${i}`} className="px-2 text-[0.8125rem] text-ink-dim">
              …
            </li>
          ) : (
            <li key={p}>
              <button
                type="button"
                onClick={() => onChange(p)}
                aria-current={p === page ? 'page' : undefined}
                className={
                  'min-w-[2rem] rounded-md px-2.5 py-1.5 text-[0.8125rem] font-semibold transition-colors ' +
                  (p === page
                    ? 'bg-brand-600 text-white shadow-card'
                    : 'border border-rule bg-surface text-ink-muted hover:border-brand-300 hover:text-ink')
                }
              >
                {p}
              </button>
            </li>
          )
        )}
      </ol>
      <button
        type="button"
        onClick={() => !nextDisabled && onChange(page + 1)}
        disabled={nextDisabled}
        className="inline-flex items-center gap-1 rounded-md border border-rule bg-surface px-3 py-1.5 text-[0.8125rem] font-semibold text-ink-muted transition-colors hover:border-brand-300 hover:text-ink disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-rule disabled:hover:text-ink-muted"
      >
        Next <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </nav>
  );
}

function pageWindow(page: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: Array<number | '…'> = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(total - 1, page + 1);
  if (start > 2) out.push('…');
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push('…');
  out.push(total);
  return out;
}

function SolicitationCard({ rlp }: { rlp: RLPOpportunity }) {
  const pendingDrafts = rlp.pending_draft_count ?? 0;
  const candidates = rlp.candidate_count ?? 0;
  const noticeType = deriveNoticeType(rlp);
  const modCount = rlp.mod_number;
  const snippet = buildSnippet(rlp);

  return (
    <article
      className="group relative overflow-hidden rounded-xl border border-rule bg-surface shadow-card transition-all hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-card-hover"
      data-testid={`rlp-row-${rlp.solicitation_number}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px]">
        {/* Main column */}
        <div className="flex flex-col gap-4 p-6">
          {/* Title + notice id */}
          <div className="flex flex-col gap-1.5">
            <Link
              to={`/rlps/${rlp.id}`}
              className="text-[1.0625rem] font-bold leading-snug tracking-head text-brand-700 hover:text-brand-800 hover:underline dark:text-brand-300 dark:hover:text-brand-200"
            >
              {rlp.title}
            </Link>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[0.8125rem]">
              <span className="font-mono text-ink">
                <span className="text-ink-muted">Notice ID:</span>{' '}
                <span className="font-semibold">{rlp.sam_notice_id || '—'}</span>
              </span>
              <span className="font-mono text-ink-muted">
                RLP {rlp.solicitation_number}
                {modCount > 0 ? <span className="text-ink-dim">-{modCount}</span> : null}
              </span>
              {modCount > 0 && <Badge tone="blue">mod {modCount}</Badge>}
            </div>
          </div>

          {/* Snippet */}
          {snippet && (
            <p className="line-clamp-2 text-[0.875rem] leading-relaxed text-ink-muted">
              {snippet}
            </p>
          )}

          {/* Dept / Subtier / Office grid */}
          <div className="grid grid-cols-1 gap-4 border-t border-rule pt-4 sm:grid-cols-3">
            <MetaField
              label="Department/Ind.Agency"
              value={rlp.agency ?? '—'}
              accent
            />
            <MetaField label="Subtier" value={rlp.sub_agency ?? '—'} accent />
            <MetaField
              label="Locale"
              value={
                rlp.city && rlp.state
                  ? `${rlp.city}, ${rlp.state}`
                  : (rlp.state ?? '—')
              }
              icon={MapPin}
            />
          </div>

          {/* Activity + status chips — our added touch */}
          <div className="flex flex-wrap items-center gap-1.5 border-t border-rule pt-4">
            <Badge
              tone={
                rlp.extraction_status === 'extracted'
                  ? 'green'
                  : rlp.extraction_status === 'failed'
                    ? 'red'
                    : 'blue'
              }
              dot
            >
              {rlp.extraction_status}
            </Badge>
            {(rlp.aboa_sf_min != null || rlp.aboa_sf_max != null) && (
              <Chip icon={Building2} tone="neutral">
                ABOA {fmtRange(rlp.aboa_sf_min, rlp.aboa_sf_max)}
              </Chip>
            )}
            <Link to={`/rlps/${rlp.id}`} className="no-underline">
              <Chip
                icon={Users}
                tone={candidates > 0 ? 'success' : 'neutral'}
                title="View candidates"
              >
                {fmtInt(candidates)} candidate{candidates === 1 ? '' : 's'}
              </Chip>
            </Link>
            {pendingDrafts > 0 && (
              <Link to="/messages" className="no-underline">
                <Chip icon={Send} tone="warn" title="Review pending drafts">
                  {fmtInt(pendingDrafts)} pending draft
                  {pendingDrafts === 1 ? '' : 's'}
                </Chip>
              </Link>
            )}
            {rlp.pdfs_downloaded > 0 && (
              <Chip icon={Paperclip} tone="info" title="PDF attachments">
                {fmtInt(rlp.pdfs_downloaded)} PDF
                {rlp.pdfs_downloaded === 1 ? '' : 's'}
              </Chip>
            )}
            <Link
              to={`/rlps/${rlp.id}`}
              className="ml-auto inline-flex items-center gap-1 text-[0.8125rem] font-semibold text-brand-700 opacity-0 transition-opacity group-hover:opacity-100 hover:text-brand-800 dark:text-brand-300 dark:hover:text-brand-200"
            >
              Open record <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {/* Right rail — mimics SAM.gov */}
        <aside className="flex flex-col gap-4 border-t border-rule bg-surface-sunk p-6 md:border-l md:border-t-0">
          <div>
            <button
              type="button"
              className="pointer-events-none inline-flex items-center gap-1.5 rounded-md border border-rule bg-surface px-2 py-1 text-[0.6875rem] font-semibold text-ink-muted"
            >
              <FileText className="h-3 w-3 text-brand-600" />
              Contract Opportunities
            </button>
          </div>

          <RailField label="Current Date Offers Due">
            <div className="flex flex-col gap-1">
              <div className="text-[0.875rem] font-bold text-ink">
                {fmtDate(rlp.response_deadline)}
              </div>
              <DeadlineBadge deadline={rlp.response_deadline} />
            </div>
          </RailField>

          <RailField label="Notice Type">
            <div className="text-[0.8125rem] font-semibold text-ink">{noticeType}</div>
          </RailField>

          <RailField label="Updated Date">
            <div className="flex items-baseline gap-1.5">
              <div className="text-[0.8125rem] font-semibold text-ink">
                {fmtDate(rlp.updated_at)}
              </div>
              {modCount > 0 && (
                <span className="font-mono text-[0.6875rem] font-semibold text-brand-700">
                  ({modCount})
                </span>
              )}
            </div>
          </RailField>

          <RailField label="Published Date">
            <div className="text-[0.8125rem] font-semibold text-ink">
              {fmtDate(rlp.posted_at)}
            </div>
          </RailField>
        </aside>
      </div>
    </article>
  );
}

function MetaField({
  label,
  value,
  accent,
  icon: Icon,
}: {
  label: string;
  value: string;
  accent?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[0.6875rem] font-medium text-ink-muted">{label}</div>
      <div
        className={
          'flex items-start gap-1.5 text-[0.8125rem] font-semibold leading-snug ' +
          (accent ? 'text-brand-700 dark:text-brand-300' : 'text-ink')
        }
      >
        {Icon ? <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-dim" /> : null}
        <span className="uppercase tracking-[0.02em]">{value}</span>
      </div>
    </div>
  );
}

function RailField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[0.6875rem] font-medium text-ink-muted">{label}</div>
      {children}
    </div>
  );
}

function Chip({
  icon: Icon,
  tone = 'neutral',
  children,
  title,
}: {
  icon: React.ComponentType<{ className?: string }>;
  tone?: 'neutral' | 'warn' | 'info' | 'success';
  children: React.ReactNode;
  title?: string;
}) {
  const cls =
    tone === 'warn'
      ? 'bg-warn-soft text-warn-bold ring-warn-border'
      : tone === 'info'
        ? 'bg-info-soft text-info-bold ring-info-border'
        : tone === 'success'
          ? 'bg-success-soft text-success-bold ring-success-border'
          : 'bg-surface-sunk text-ink-muted ring-rule';
  return (
    <span
      title={title}
      className={
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold ring-1 ring-inset ' +
        cls
      }
    >
      <Icon className="h-3 w-3" />
      {children}
    </span>
  );
}

function deriveNoticeType(rlp: RLPOpportunity): string {
  if (rlp.mod_number > 0) return 'Updated Solicitation';
  if (rlp.extraction_status === 'extracted') {
    return 'Combined Synopsis/Solicitation';
  }
  return 'Solicitation';
}

function buildSnippet(rlp: RLPOpportunity): string {
  const parts: string[] = [];
  if (rlp.city && rlp.state) parts.push(`${rlp.city}, ${rlp.state}`);
  if (rlp.aboa_sf_min != null || rlp.aboa_sf_max != null) {
    parts.push(`ABOA ${fmtRange(rlp.aboa_sf_min, rlp.aboa_sf_max)}`);
  }
  if (rlp.sub_agency) parts.push(`Requestor: ${rlp.sub_agency}`);
  if (parts.length === 0) return '';
  return parts.join(' · ') + '.';
}
